import {
    AppShell,
    Avatar,
    Box,
    Burger,
    Group,
    NavLink as MantineNavLink,
    Stack,
    Text,
    ThemeIcon,
    UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
    AdminCapabilityEnum,
    AdminSectionEnum,
} from '@routellers/types/dist/admin/admin-permissions';
import {
    IconBook,
    IconBuildingCommunity,
    IconBuildingStore,
    IconClipboardCheck,
    IconList,
    IconCoffee,
    IconLanguage,
    IconLayoutDashboard,
    IconLogout,
    IconMap2,
    IconMapPin,
    IconMapRoute,
    IconMessageStar,
    IconPackage,
    IconReceipt2,
    IconRoute,
    IconTicket,
    IconUserCircle,
    IconUsers,
} from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { useAuth } from '../providers/auth-context';
import { adminCan } from '../utils/admin-permissions';
import { LanguageSwitcher } from './language-switcher';

const links: Array<{
    to: string;
    navKey: keyof typeof import('../i18n/en').en.nav;
    icon: typeof IconLayoutDashboard;
    end: boolean;
    section: AdminSectionEnum;
}> = [
    {
        to: '/',
        navKey: 'dashboard',
        icon: IconLayoutDashboard,
        end: true,
        section: AdminSectionEnum.DASHBOARD,
    },
    {
        to: '/cities',
        navKey: 'cities',
        icon: IconBuildingCommunity,
        end: false,
        section: AdminSectionEnum.CITIES,
    },
    {
        to: '/quests',
        navKey: 'quests',
        icon: IconMapRoute,
        end: false,
        section: AdminSectionEnum.QUESTS,
    },
    {
        to: '/routes',
        navKey: 'routes',
        icon: IconRoute,
        end: false,
        section: AdminSectionEnum.ROUTES,
    },
    {
        to: '/people',
        navKey: 'people',
        icon: IconUsers,
        end: false,
        section: AdminSectionEnum.PEOPLE,
    },
    {
        to: '/attractions',
        navKey: 'attractions',
        icon: IconMapPin,
        end: false,
        section: AdminSectionEnum.ATTRACTIONS,
    },
    {
        to: '/collections',
        navKey: 'collections',
        icon: IconList,
        end: false,
        section: AdminSectionEnum.ATTRACTIONS,
    },
    {
        to: '/establishments',
        navKey: 'establishments',
        icon: IconCoffee,
        end: false,
        section: AdminSectionEnum.ESTABLISHMENTS,
    },
    {
        to: '/authors',
        navKey: 'authors',
        icon: IconUserCircle,
        end: false,
        section: AdminSectionEnum.AUTHORS,
    },
    {
        to: '/venues',
        navKey: 'venues',
        icon: IconBuildingStore,
        end: false,
        section: AdminSectionEnum.VENUES,
    },
    {
        to: '/activities',
        navKey: 'activities',
        icon: IconTicket,
        end: false,
        section: AdminSectionEnum.ACTIVITIES,
    },
    {
        to: '/orders',
        navKey: 'orders',
        icon: IconPackage,
        end: false,
        section: AdminSectionEnum.ORDERS,
    },
    {
        to: '/purchases',
        navKey: 'purchases',
        icon: IconReceipt2,
        end: false,
        section: AdminSectionEnum.PURCHASES,
    },
    {
        to: '/reviews',
        navKey: 'reviews',
        icon: IconMessageStar,
        end: false,
        section: AdminSectionEnum.REVIEWS,
    },
    {
        to: '/translations',
        navKey: 'translations',
        icon: IconLanguage,
        end: false,
        section: AdminSectionEnum.TRANSLATIONS,
    },
    {
        to: '/qa',
        navKey: 'qa',
        icon: IconClipboardCheck,
        end: false,
        section: AdminSectionEnum.QA,
    },
    {
        to: '/users',
        navKey: 'users',
        icon: IconMap2,
        end: false,
        section: AdminSectionEnum.USERS,
    },
];

export const AppLayout = () => {
    const { t } = useTranslation();
    const { admin, logout } = useAuth();
    const [opened, { toggle, close }] = useDisclosure();
    const navigate = useNavigate();
    const visibleLinks = useMemo(
        () => links.filter((link) => adminCan(admin, link.section, AdminCapabilityEnum.VIEW)),
        [admin],
    );

    return (
        <AppShell
            header={{ height: 60 }}
            navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !opened } }}
            padding='lg'
            bg='gray.0'
        >
            <AppShell.Header>
                <Group h='100%' px='md' justify='space-between'>
                    <Group gap='xs'>
                        <Burger opened={opened} onClick={toggle} hiddenFrom='sm' size='sm' />
                        <ThemeIcon size={30} radius='md' variant='filled' color='brand'>
                            <IconMapRoute size={18} />
                        </ThemeIcon>
                        <Box visibleFrom='xs'>
                            <Text fw={700} size='sm' lh={1.1}>
                                Routellers
                            </Text>
                            <Text size='xs' c='dimmed' lh={1.1}>
                                {t('layout.admin')}
                            </Text>
                        </Box>
                    </Group>

                    <Group gap='sm'>
                        <LanguageSwitcher />
                        <Avatar color='brand' radius='xl' size='sm'>
                            {admin?.name?.charAt(0)?.toUpperCase() || 'A'}
                        </Avatar>
                    </Group>
                </Group>
            </AppShell.Header>

            <AppShell.Navbar p='sm'>
                <Stack justify='space-between' h='100%'>
                    <Box>
                        {visibleLinks.map((link) => (
                            <MantineNavLink
                                key={link.to}
                                component={NavLink}
                                to={link.to}
                                end={link.end}
                                label={t(`nav.${link.navKey}`)}
                                onClick={close}
                                leftSection={<link.icon size={18} stroke={1.7} />}
                                styles={{ root: { borderRadius: 8 } }}
                            />
                        ))}
                        <MantineNavLink
                            component={NavLink}
                            to='/wiki'
                            end={false}
                            label={t('nav.wiki')}
                            onClick={close}
                            leftSection={<IconBook size={18} stroke={1.7} />}
                            styles={{ root: { borderRadius: 8 } }}
                        />
                    </Box>

                    <UnstyledButton
                        onClick={async () => {
                            await logout();
                            navigate('/login');
                        }}
                        p='sm'
                    >
                        <Group gap='xs'>
                            <IconLogout size={18} stroke={1.7} />
                            <Text size='sm'>{t('common.logout')}</Text>
                        </Group>
                    </UnstyledButton>
                </Stack>
            </AppShell.Navbar>

            <AppShell.Main>
                <Box
                    maw={1200}
                    mx='auto'
                    mih='calc(100dvh - var(--app-shell-header-offset, 60px) - 2 * var(--mantine-spacing-lg))'
                    style={{ display: 'flex', flexDirection: 'column' }}
                >
                    <Outlet />
                </Box>
            </AppShell.Main>
        </AppShell>
    );
};
